import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_URL = '/api/masters/port-remarks/';

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json',
});

const DATE_FIELD_OPTIONS = [
    { id: 'discharge_date', text: 'Discharge Date' },
    { id: 'carry_in_end_date', text: 'Carry In End Date' },
    { id: 'stripping_date', text: 'Stripping Date' },
];

const getTextColorForBackground = (hexColor) => {
    if (!hexColor) return '#000000';
    const rgb = parseInt(hexColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 128 ? '#FFFFFF' : '#000000';
};

export default function PortRemarksConfiguration() {
    const [remarks, setRemarks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [newRemark, setNewRemark] = useState({ text: '', date_field_to_map: null, color: '#FFFFFF' });
    const [editedRemark, setEditedRemark] = useState({ text: '', date_field_to_map: null, color: '#FFFFFF' });
    // --- FIX: Wrap hasPermission to allow alternative port config menu permission to act as PORTREMARKS ---
    const { hasPermission: rawHasPermission } = usePermissions();
    const hasPermission = (module, action) => {
        if (module === 'PORTREMARKS') {
            return rawHasPermission('PORTREMARKS', action) || rawHasPermission('PORT CONFIGURATIONS', action);
        }
        return rawHasPermission(module, action);
    };

    const fetchRemarks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API_URL, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setRemarks(data.results || data);
            } else {
                toast.error('Failed to fetch remarks.');
            }
        } catch (e) {
            toast.error('An error occurred.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRemarks();
    }, [fetchRemarks]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newRemark.text.trim()) {
            toast.warn('Remark text cannot be empty.');
            return;
        }
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ ...newRemark, text: newRemark.text.toUpperCase() }),
            });
            if (res.ok) {
                toast.success('Remark added.');
                setNewRemark({ text: '', date_field_to_map: null, color: '#FFFFFF' });
                fetchRemarks();
            } else {
                const err = await res.json();
                toast.error(err.text?.[0] || 'Failed to add remark.');
            }
        } catch (e) { toast.error('An error occurred.'); }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this remark?')) {
            try {
                const res = await fetch(`${API_URL}${id}/`, { method: 'DELETE', headers: getAuthHeaders() });
                if (res.status === 204) {
                    toast.success('Remark deleted.');
                    fetchRemarks();
                } else {
                    toast.error('Failed to delete remark.');
                }
            } catch (e) { toast.error('An error occurred.'); }
        }
    };

    const startEdit = (remark) => {
        setEditingId(remark.id);
        setEditedRemark({ text: remark.text, date_field_to_map: remark.date_field_to_map, color: remark.color || '#FFFFFF' });
    };

    const handleSaveEdit = async (id) => {
        if (!editedRemark.text.trim()) {
            toast.warn('Remark text cannot be empty.');
            return;
        }
        try {
            const res = await fetch(`${API_URL}${id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ ...editedRemark, text: editedRemark.text.toUpperCase() }),
            });
            if (res.ok) {
                toast.success('Remark updated.');
                setEditingId(null);
                fetchRemarks();
            } else {
                const err = await res.json();
                toast.error(err.text?.[0] || 'Failed to update remark.');
            }
        } catch (e) { toast.error('An error occurred.'); }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div>
            {/* 3. Protect Add form */}
            {hasPermission('PORTREMARKS', 'can_create') && (
                <>
                    <h4>Add New Port Remark</h4>
                    <form onSubmit={handleAdd} style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                        <input
                            type="text"
                            value={newRemark.text}
                            onChange={(e) => setNewRemark(p => ({ ...p, text: e.target.value }))}
                            placeholder="Enter new remark text"
                            className="search-input"
                            style={{ flex: 2, backgroundColor: newRemark.color, color: getTextColorForBackground(newRemark.color) }}
                        />
                        <Select2Dropdown
                            options={DATE_FIELD_OPTIONS}
                            value={newRemark.date_field_to_map}
                            onChange={val => setNewRemark(p => ({ ...p, date_field_to_map: val }))}
                            placeholder="Map to Date (Optional)"
                            valueKey="id"
                            displayKey="text"
                            style={{ flex: 1 }}
                            clearable
                        />
                        <input
                            type="color"
                            value={newRemark.color}
                            onChange={(e) => setNewRemark(p => ({ ...p, color: e.target.value }))}
                            title="Select Remark Color"
                            style={{ width: '60px', height: '38px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                        />
                        <button type="submit" className="add-btn">Add</button>
                    </form>
                </>
            )}

            <h4>Existing Remarks</h4>
            <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                {remarks.map(remark => (
                    <div key={remark.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderBottom: '1px solid #eee' }}>
                        {editingId === remark.id ? (
                            <>
                                <input type="text" value={editedRemark.text} onChange={e => setEditedRemark(p => ({ ...p, text: e.target.value }))} className="search-input" style={{ flex: 2, backgroundColor: editedRemark.color, color: getTextColorForBackground(editedRemark.color) }} />
                                <Select2Dropdown
                                    options={DATE_FIELD_OPTIONS}
                                    value={editedRemark.date_field_to_map}
                                    onChange={val => setEditedRemark(p => ({ ...p, date_field_to_map: val }))}
                                    valueKey="id"
                                    displayKey="text"
                                    style={{ flex: 1 }}
                                    clearable
                                />
                                <input
                                    type="color"
                                    value={editedRemark.color}
                                    onChange={(e) => setEditedRemark(p => ({ ...p, color: e.target.value }))}
                                    title="Select Remark Color"
                                    style={{ width: '60px', height: '38px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                                />
                                {/* 4. Protect Save button */}
                                {hasPermission('PORTREMARKS', 'can_edit') && (
                                    <button className="icon-btn save-btn" onClick={() => handleSaveEdit(remark.id)}><i className="fa fa-save" /></button>
                                )}
                                <button className="icon-btn cancel-btn" onClick={() => setEditingId(null)}><i className="fa fa-times" /></button>
                            </>
                        ) : (
                            <>
                                <strong style={{ flex: 2, backgroundColor: remark.color || '#FFFFFF', color: getTextColorForBackground(remark.color || '#FFFFFF'), padding: '4px 8px', borderRadius: '4px' }}>{remark.text}</strong>
                                <span style={{ flex: 1, color: '#555' }}>{DATE_FIELD_OPTIONS.find(o => o.id === remark.date_field_to_map)?.text || 'Not Mapped'}</span>
                                {/* 5. Protect Edit and Delete buttons */}
                                {hasPermission('PORTREMARKS', 'can_edit') && (
                                    <button className="icon-btn" onClick={() => startEdit(remark)}><i className="fa fa-pencil" /></button>
                                )}
                                {hasPermission('PORTREMARKS', 'can_delete') && (
                                    <button className="icon-btn cancel-btn" onClick={() => handleDelete(remark.id)}><i className="fa fa-trash" /></button>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}